import _ from 'lodash';
import DiscussionService from '../components/discussion/discussion.service';
import DiscussionCategoryService from '../components/discussion/discussion-category.service';
import DiscussionCtrl from '../discussion/home/discussion.controller';
import discussionTemplate from '../discussion/home/discussion.html';
import DiscussionCategoryCtrl from '../discussion/category/category.controller';
import discussionCategoryTemplate from '../discussion/category/category.html';
import SingleDiscussionCtrl from '../discussion/single/single-discussion.controller';
import singleDiscussionTemplate from '../discussion/single/single-discussion.html';
import NewDiscussionCtrl from '../discussion/new-discussion.controller';
import newDiscussionTemplate from '../discussion/new-discussion.html';
import EditDiscussionCtrl from '../discussion/edit-discussion.controller';
import editDiscussionTemplate from '../discussion/edit-discussion.html';
import UserAuthenticator from '../authentication/user-authenticator';
import AdminAuthenticator from '../authentication/admin-authenticator';
import MyDiscussionsCtrl from '../discussion/my-discussions.controller';
import myDiscussionsTemplate from '../discussion/my-discussions.html';
import TransferDiscussionCtrl from '../discussion/transfer/transfer-discussion.controller';
import transferDiscussionTemplate from '../discussion/transfer/transfer-discussion.html';
import { redirectByDesign, useNewDesign } from './design-refresh';

const RANDOM_DISCUSSIONS = ['DiscussionService', DiscussionService => DiscussionService.random()];

const FORUMS_HEADER_DATA = ['StaticValuesService', (StaticValuesService) => {
  const defaultData = {
    title: 'Software Testing Forums',
    description: 'Network with your peers, debate hot topics and exchange knowledge with top minds in the industry. Start a discussion or join one that’s already going on.',
    coverBg: {
      small: '/utest-static/assets/images/hero/discussions-bg-xs.jpg',
      large: '/utest-static/assets/images/hero/discussions-bg.jpg',
    },
  };
  return StaticValuesService.pageData('forums').then((response) => {
    response.data = _.defaultsDeep(response.data, defaultData);
    return response;
  }, _error => ({
    data: defaultData,
  }));
}];

const DiscussionRoutes = {
  route: (module) => {
    module
      .factory('DiscussionService', DiscussionService)
      .service('DiscussionCategoryService', DiscussionCategoryService)
      .controller('DiscussionCtrl', DiscussionCtrl)
      .controller('DiscussionCategoryCtrl', DiscussionCategoryCtrl)
      .controller('SingleDiscussionCtrl', SingleDiscussionCtrl)
      .controller('NewDiscussionCtrl', NewDiscussionCtrl)
      .controller('EditDiscussionCtrl', EditDiscussionCtrl)
      .controller('MyDiscussionsCtrl', MyDiscussionsCtrl)
      .controller('TransferDiscussionCtrl', TransferDiscussionCtrl);

    module.config(['$stateProvider', ($stateProvider) => {
      $stateProvider
        .state('discussions', {
          url: '/forums?:tab&:topic_page&:category_id',
          controller: ['$state', '$scope', function ($state, $scope) { $scope.$state = $state; }],
          abstract: true,
          template: '<div ui-view="discussions-index" ng-show="$state.includes(\'discussion\')"></div><div ui-view="discussions-show" ng-show="$state.includes(\'singlediscussionpage\')" ></div>',
          parent: 'root',
          authenticated: UserAuthenticator,
        })
        .state('discussion', {
          url: '',
          parent: 'discussions',
          sticky: false,
          // With the flag on, the 2026 `forums` state takes over the same URL.
          redirectTo: redirectByDesign('forums', null),
          views: {
            'discussions-index': {
              template: discussionTemplate,
              controller: 'DiscussionCtrl',
            },
          },
          resolve: {
            randomDiscussions: RANDOM_DISCUSSIONS,
            headerData: FORUMS_HEADER_DATA,
            coverBg: ['ImageDataService', 'headerData', (ImageDataService, headerData) => (headerData.data.coverBg.small === '/utest-static/assets/images/hero/discussions-bg-xs.jpg'
              ? ImageDataService.getData(headerData.data.coverBg.small) : headerData.data.coverBg.small)],
            title: [() => 'uTest Community Forum'],
            authenticated: UserAuthenticator,
          },
        })
        .state('singlediscussion', {
          url: '/:discussionId',
          parent: 'discussions',
          reloadOnSearch: false,
          resolve: {
            discussion: ['$transition$', function ($transition$) {
              return $transition$.params().discussionId;
            }],
            title: ['discussion', discussion => `uTest Community Forum - ${discussion.data.title}`],
            authenticated: UserAuthenticator,
          },
          views: {
            'discussions-show': {
              controller: 'SingleDiscussionCtrl',
            },
          },
        })
        .state('singlediscussionpage', {
          url: '/:discussionId/page/:page?sort',
          parent: 'discussions',
          reloadOnSearch: false,
          sticky: false,
          resolve: {
            discussion: ['$transition$', 'DiscussionService', function ($transition$, DiscussionService) {
              return DiscussionService.find($transition$.params().discussionId);
            }],
            title: ['discussion', discussion => `uTest Community Forum - ${discussion.data.title}`],
            authenticated: UserAuthenticator,
            useNewDesign,
          },
          views: {
            'discussions-show': {
              template: `
                <ut-topic-page ng-if="$resolve.useNewDesign" [discussion]="$resolve.discussion.data" [user]="$resolve.user"></ut-topic-page>
                <div ng-if="!$resolve.useNewDesign">${singleDiscussionTemplate}</div>
              `,
              controllerProvider: ['useNewDesign', isNewDesign => (isNewDesign ? null : 'SingleDiscussionCtrl')],
            },
          },
        })
        .state('discussioncategory', {
          url: '/forums/category/:categoryId',
          template: `
            <ut-forums-page ng-if="$resolve.useNewDesign" [user]="$resolve.user" [category]="$resolve.discussionCategory.data"></ut-forums-page>
            <div ng-if="!$resolve.useNewDesign">${discussionCategoryTemplate}</div>
          `,
          controllerProvider: ['useNewDesign', isNewDesign => (isNewDesign ? null : 'DiscussionCategoryCtrl')],
          parent: 'root',
          resolve: {
            discussionCategory: ['DiscussionCategoryService', '$transition$', function (DiscussionCategoryService, $transition$) {
              return DiscussionCategoryService.find($transition$.params().categoryId);
            }],
            title: ['discussionCategory', discussionCategory => `uTest Community Forum - ${discussionCategory.data.name}`],
            authenticated: UserAuthenticator,
            useNewDesign,
          },
        })
        .state('mydiscussions', {
          url: '/my-forums',
          template: `
            <ut-my-forums-page ng-if="$resolve.useNewDesign" [user]="$resolve.user"></ut-my-forums-page>
            <div ng-if="!$resolve.useNewDesign">${myDiscussionsTemplate}</div>
          `,
          // No legacy controller with the new page, so the topics aren't loaded twice.
          controllerProvider: ['useNewDesign', isNewDesign => (isNewDesign ? null : 'MyDiscussionsCtrl')],
          parent: 'root',
          resolve: {
            user: UserAuthenticator,
            title: [() => 'uTest Community - My Discussions'],
            authenticated: UserAuthenticator,
            useNewDesign,
          },
        })
        .state('newDiscussion', {
          // ?categoryId pre-selects the category, from a category page's "Create a new topic".
          url: '/new/forums?categoryId',
          template: `
            <ut-topic-form-page ng-if="$resolve.useNewDesign"></ut-topic-form-page>
            <div ng-if="!$resolve.useNewDesign">${newDiscussionTemplate}</div>
          `,
          controllerProvider: ['useNewDesign', isNewDesign => (isNewDesign ? null : 'NewDiscussionCtrl')],
          parent: 'root',
          resolve: {
            user: UserAuthenticator,
            title: [() => 'uTest Community Forum - Create Discussion Topic'],
            authenticated: UserAuthenticator,
            useNewDesign,
          },
        })
        .state('editDiscussion', {
          url: '/forums/:discussionId/edit',
          template: `
            <ut-topic-form-page ng-if="$resolve.useNewDesign" [discussion]="$resolve.discussion.data"></ut-topic-form-page>
            <div ng-if="!$resolve.useNewDesign">${editDiscussionTemplate}</div>
          `,
          controllerProvider: ['useNewDesign', isNewDesign => (isNewDesign ? null : 'EditDiscussionCtrl')],
          parent: 'root',
          resolve: {
            discussion: ['$transition$', 'DiscussionService', function ($transition$, DiscussionService) {
              return DiscussionService.find($transition$.params().discussionId);
            }],
            user: UserAuthenticator,
            title: [() => 'uTest Community Forum - Edit Discussion Topic'],
            authenticated: UserAuthenticator,
            useNewDesign,
          },
        })
        .state('transferDiscussions', {
          url: '/transfer/forums',
          template: transferDiscussionTemplate,
          controller: 'TransferDiscussionCtrl',
          parent: 'root',
          resolve: {
            user: UserAuthenticator,
            title: [() => 'uTest Community - Transfer Discussion Topic'],
            authenticated: UserAuthenticator,
            adminAuthenticated: AdminAuthenticator,
          },
        })
        // The 2026 forum index (see `discussion`). Same URL, but its params are dynamic, so a tab, page or
        // category change updates the page in place instead of re-entering the state. Registered last, so the
        // legacy state still wins URL matching and redirects here with the flag on.
        .state('forums', {
          url: '/forums?tab&topic_page&category_id',
          parent: 'root',
          params: {
            tab: { dynamic: true },
            topic_page: { dynamic: true },
            category_id: { dynamic: true },
          },
          redirectTo: redirectByDesign(null, 'discussion'),
          template: '<ut-forums-page [user]="$resolve.user" [header-data]="$resolve.headerData.data" [featured]="$resolve.randomDiscussions.data"></ut-forums-page>',
          resolve: {
            // Featured topics are optional here: a failed request shouldn't fail the whole page.
            randomDiscussions: ['DiscussionService', DiscussionService => DiscussionService.random().catch(() => ({ data: [] }))],
            headerData: FORUMS_HEADER_DATA,
            title: [() => 'uTest Community Forum'],
            authenticated: UserAuthenticator,
          },
        });
    }]);
  },
};

export default DiscussionRoutes;
